import { useState } from 'react';
import { Button } from '@design-system/components/button/button';
import { Tooltip } from '@design-system/components/tooltip/tooltip';
import { Typography } from '@design-system/components/typography/typography';
import { faRocket } from '@fortawesome/pro-solid-svg-icons';
import { ChecklistProgress } from './ChecklistProgress';
import { Strategy } from '../model';
import * as styles from './GuideHeader.css';

type GuideHeaderProps = {
  strategy: Strategy | null;
  completedChecklistsCount: number;
  totalChecklistsCount: number;
  totalEstimatedTime: string;
  isLive: boolean;
  canGoLive: boolean;
  onGoLive: () => void;
  onReview: () => void;
};

const GoLiveTooltipContent = ({ onDismiss }: { onDismiss: () => void }) => (
  <div style={{ padding: '12px 16px', maxWidth: '300px' }}>
    <Typography variant="body" size="medium" fontWeight="bold" color="W-100">
      Going Live
    </Typography>
    <Typography
      variant="body"
      size="small"
      color="N-20"
      style={{ marginTop: '8px' }}
    >
      Going live means your app will be available to users and you'll be able to
      start selling your products & offers.
      <br />
      <br />
      It gets enabled when you have at least one offer published & ready for
      sale.
    </Typography>
    <span
      onClick={onDismiss}
      style={{
        display: 'inline-block',
        marginTop: '16px',
        color: 'rgb(147, 109, 216)',
        fontSize: '12px',
        cursor: 'pointer',
      }}
    >
      Ok, get it
    </span>
  </div>
);

export const GuideHeader = ({
  strategy,
  completedChecklistsCount,
  totalChecklistsCount,
  totalEstimatedTime,
  isLive,
  canGoLive,
  onGoLive,
  onReview,
}: GuideHeaderProps) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  return (
    <div className={styles.headerCard}>
      <div className={styles.headerGrid}>
        {/* Strategy */}
        <div className={styles.statColumn}>
          <span className={styles.statLabel}>Strategy</span>
          <span className={styles.statValue}>{strategy?.name || '-'}</span>
          {strategy ? (
            <button
              type="button"
              onClick={onReview}
              className={styles.reviewLink}
            >
              Review
            </button>
          ) : (
            <button
              type="button"
              onClick={onReview}
              className={styles.reviewLink}
            >
              Select strategy
            </button>
          )}
        </div>

        {/* Checklists Completed */}
        <div className={styles.statColumn}>
          <span className={styles.statLabel}>Checklists Completed</span>
          <div className={styles.statItems}>
            <span className={styles.statValue}>
              {completedChecklistsCount}/{totalChecklistsCount}
            </span>
          </div>
          <div className={styles.progressContainer}>
            <ChecklistProgress
              completed={completedChecklistsCount}
              total={totalChecklistsCount}
              showLabel={false}
              size="small"
            />
          </div>
        </div>

        {/* Estimated Launch Time */}
        <div className={styles.statColumn}>
          <span className={styles.statLabel}>Estimated Launch In</span>
          <span className={styles.statValue}>{totalEstimatedTime}</span>
        </div>

        {/* Go Live Button */}
        {!isLive && (
          <div className={styles.buttonContainer}>
            {!canGoLive ? (
              <Tooltip
                variant="dark"
                placement="right"
                open={tooltipOpen}
                onOpen={() => setTooltipOpen(true)}
                onClose={() => setTooltipOpen(false)}
                title={
                  <GoLiveTooltipContent
                    onDismiss={() => setTooltipOpen(false)}
                  />
                }
                custom
              >
                <span>
                  <Button
                    color="success"
                    leftIcon={faRocket}
                    onClick={onGoLive}
                    size="medium"
                    disabled
                  >
                    Go live
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                color="success"
                leftIcon={faRocket}
                onClick={onGoLive}
                size="medium"
              >
                Go live
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
